import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { DataService as CoreDataService, Query, QueryColumn, QueryColumnSortOrder } from '@barthauer/abc/core/data';
import { _AbcButtonComponent, _AbcGalleryComponent, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { Person, SubProject } from '@barthauer/custom/xmt-models';
import {
  DxAccordionModule,
  DxBulletModule,
  DxDataGridModule,
  DxGalleryComponent,
  DxGalleryModule,
  DxScrollViewModule,
  DxTooltipModule,
} from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';
/* import pptxgen from 'pptxgenjs'; */
import { Subscription } from 'rxjs';
import { AppService } from '../../../services/app.service';
import { DataService, ObjectKeys } from '../../../services/data.service';
import { SharedModule } from '../../../shared/shared.module';
import { SubProjectViewModel } from '../../../view-models/sub-project.view-model';
import { SubProjectService } from '../sub-project.service';
import { ROLES } from '../../../models/enumeration-list.model';

@Component({
  selector: 'ubt-geods-sub-project-detail',
  templateUrl: './sub-project-detail.component.html',
  styleUrls: ['./sub-project-detail.component.scss'],
  imports: [
    CommonModule,
    DxAccordionModule,
    DxScrollViewModule,
    DxBulletModule,
    DxTooltipModule,
    DxGalleryModule,
    SharedModule,
    DxDataGridModule,
    _AbcGridComponent,
    _AbcGalleryComponent,
    _AbcButtonComponent,
  ],
  providers: [SubProjectService],
  standalone: true,
})
export class SubProjectDetailComponent implements OnInit, OnDestroy {
  @ViewChild(DxGalleryComponent, { static: false }) gallery: _AbcGalleryComponent;
  @ViewChild('subProjectReport', { static: false }) subProjectReport: _AbcGridComponent;
  public disablePtxExport = false;
  public subProjectTitle: string;
  private subProjectLabel: string;
  public subprojectsId = [];
  public showReportsDetails: boolean;
  public columnsHeader: Column[] = [
    {
      dataField: 'measureTitle',
      caption: this.dataService.res('Ubt-CreateMeasureTask-Measure'),
      width: '40%',
    },
    {
      dataField: 'progressPercentage',
      cellTemplate: 'progressCellTemplate',
      caption: this.dataService.res('Ubt-MeasureDetail-Chart-Progress'),
      width: '15%',
    },
    {
      dataField: 'maturityLevel',
      alignment: 'left',
      caption: this.dataService.res('Ubt-MeasureDetail-Maturity-Level'),
      width: '15%',
    },

    {
      dataField: 'totalTime',
      dataType: 'number',
      format: '#,##0 h',
      caption: this.dataService.res('Ubt-CreateMeasureTask-Total-Time'),
      width: '15%',
    },
    {
      dataField: 'executedTime',
      dataType: 'number',
      format: '#,##0 h',
      width: '15%',
      caption: this.dataService.res('Ubt-CreateMeasureTask-Executed-Time'),
    },
  ];
  private measureTasks = [];
  private subProjectId: string;
  public measureColumns: QueryColumn[] = [];
  public measureTaskColumns: QueryColumn[] = [];
  public reports = [];
  public subProjectColumns: QueryColumn[] = [];
  private subProjectResponsible: Person;
  public subProjectsViewModel: SubProjectViewModel[] = [];
  private subscriptions: Subscription[] = [];
  public ROLES=ROLES
  constructor(
    public subProjectService: SubProjectService,
    private router: Router,
    public appService: AppService,
    private activatedRoute: ActivatedRoute,
    public dataService: DataService,
    private coreDataService: CoreDataService
  ) {
    this.subProjectId = this.activatedRoute.snapshot.params.id;
    sessionStorage.setItem('subProjectId', this.subProjectId);

    this.subscriptions.push(
      this.router.events.subscribe((e: Event) => {
        if (e instanceof NavigationEnd && e.url.startsWith('/subProjectDetail')) {
          this.prepareSubProjectDetailReport();
        }
      })
    );
  }

  public customizeTooltip = (pointsInfo) => ({ text: `${parseInt(pointsInfo.originalValue)}%` });

  public ngOnInit(): void {
    this.appService.showLoader(true);

    if (this.appService.subProjectIds.length === 0) {
      this.prepareAllSubProjects();
    }

    if (this.dataService.cachedResponsiblesResponse.length === 0) {
      this.subProjectColumns.push(
        this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
        this.coreDataService.createQueryColumn('ParentId', 'ParentId', QueryColumnSortOrder.None),
        this.coreDataService.createQueryColumn('IdRefresponsible', 'IdRefResponsible', QueryColumnSortOrder.None),
        this.coreDataService.createQueryColumn(
          'IdRefResponsibleSupport',
          'IdRefResponsibleSupport',
          QueryColumnSortOrder.None
        ),
        this.coreDataService.createQueryColumn('Highlights', 'Highlights', QueryColumnSortOrder.None),
        this.coreDataService.createQueryColumn('Lowlights', 'Lowlights', QueryColumnSortOrder.None),
        this.coreDataService.createQueryColumn(
          'ImplementationStatus',
          'ImplementationStatus',
          QueryColumnSortOrder.None
        ),
        this.coreDataService.createQueryColumn('SysDateInsert', 'SysDateInsert', QueryColumnSortOrder.None)
      );
      //const oPath = 'ParentId=' + "'" + localStorage.getItem('projectId') + "'";
      this.dataService.prepareStaffMembers().subscribe((data) => (this.dataService.cachedResponsiblesResponse = data));
    }
  }

  public onCellPrepared = (e) => {
    if (e.rowType === 'header') {
      e.cellElement.style.textAlign = 'left';
    }
  };

  private prepareSubProjectDetailReport(): void {
    this.appService.showLoader(true);

    this.prepareSubProjectDetailColumns();
    this.coreDataService.executeReadObjectsQuery(this.appService.subProjectRequestColumns).subscribe(
      (subProject) => {
        this.subProjectService.subProjectReports = [];
        this.reports = [];
        const subProjectreport = new SubProjectReport(null);
        subProjectreport.implementationStatus = subProject[0].ImplementationStatus;
        subProjectreport.subProjectLabel = subProject[0].Label;
        this.subProjectLabel = subProject[0].Label;

        if (subProject[0].xmtMeasure) {
          this.disablePtxExport = false;
          subProject[0].xmtMeasure.forEach((measure) => {
            const report = new SubProjectReport(null);
            report.implementationStatus = subProject[0].ImplementationStatus;
            report.subProjectLabel = subProject[0].Label;
            report.measureId = measure.Id;
            report.measureTitle = measure.Title;
            if (
              Array.isArray(this.dataService.cachedResponsiblesResponse) &&
              this.dataService.cachedResponsiblesResponse.length > 0 &&
              Array.isArray(subProject) &&
              subProject.length > 0
            ) {
              this.subProjectResponsible = this.dataService.cachedResponsiblesResponse?.find(
                (item) => item.Id === subProject[0].IdRefResponsible
              );
            }
            if (measure.xmtMeasureProgress) {
              report.maturityLevel = measure.xmtMeasureProgress[0].MaturityLevel || 'N/A';
              report.progressPercentage = measure.xmtMeasureProgress[0].ProgressPercentage;
              report.decision = measure.xmtMeasureProgress[0].Decision;
              report.nextSteps = measure.xmtMeasureProgress[0].NextSteps;
              report.progress = measure.xmtMeasureProgress[0].Progress;
              report.riskAndChance = measure.xmtMeasureProgress[0].RiskAndChance;
            }
            if (measure.xmtMeasureTask) {
              const measureTasks = measure.xmtMeasureTask;
              measureTasks.forEach((measureTask) => {
                report.totalTime = report.totalTime + measureTask.TotalTimeRequired || report.totalTime + 0;
                report.executedTime = report.executedTime + measureTask.TotalTimeExecuted || report.executedTime + 0;
              });
            }
            this.subProjectService.subProjectReports.push(report);
          });
        } else {
          this.disablePtxExport = true;
          this.subProjectService.subProjectReports.push(subProjectreport);
        }
        this.subProjectService.subProjectReports = Object.entries(this.subProjectService.subProjectReports);

        this.subProjectService.subProjectReports.forEach((data) => {
          this.reports.push(data[1]);
          this.displayReport();
        });
      },
      (error) => {
        console.error(error);
      },
      () => {
        this.appService.showLoader(false);
      }
    );
  }

  private displayReport() {
    switch (this.reports.length) {
      case 0:
        this.showReportsDetails = false;
        break;
      case 1:
        if (
          !this.reports[0].progress &&
          !this.reports[0].nextSteps &&
          !this.reports[0].decision &&
          !this.reports[0].riskAndChance
        ) {
          this.showReportsDetails = false;
        } else {
          this.showReportsDetails = true;
        }
        break;
      default:
        this.showReportsDetails = true;
    }
  }

  private prepareSubProjectDetailColumns(): void {
    const project: Query = new Query();
    const projectColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Designation', 'Label', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('IdRefResponsible', 'IdRefResponsible', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn(
        'IdRefResponsibleSupport',
        'IdRefResponsibleSupport',
        QueryColumnSortOrder.None
      ),
      this.coreDataService.createQueryColumn('Highlights', 'Highlights', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('Lowlights', 'Lowlights', QueryColumnSortOrder.None),
      this.coreDataService.createQueryColumn('ImplementationStatus', 'ImplementationStatus', QueryColumnSortOrder.None),
    ];
    project.ObjectType = ObjectKeys.SUBPROJECT;
    project.Columns = projectColumns;
    project.OPath = 'Id=' + "'" + sessionStorage.getItem('subProjectId') + "'";

    const measure: Query = new Query();
    measure.Name = 'xmtMeasure';
    measure.OPath = 'measures';
    const measureColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Designation', 'Title'),
    ];

    measure.Columns = measureColumns;

    const measureProgress: Query = new Query();
    measureProgress.Name = 'xmtMeasureProgress';
    measureProgress.OPath = 'MeasureProgresses';
    const measureProgressColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Decision', 'Decision'),
      this.coreDataService.createQueryColumn('MaturityLevel', 'MaturityLevel'),
      this.coreDataService.createQueryColumn('NextSteps', 'NextSteps'),
      this.coreDataService.createQueryColumn('Opportunities', 'Oppotunities'),
      this.coreDataService.createQueryColumn('Progress', 'Progress'),
      this.coreDataService.createQueryColumn('ProgressDate', 'ProgressDate'),
      this.coreDataService.createQueryColumn('ProgressPercentage', 'ProgressPercentage'),
      this.coreDataService.createQueryColumn('RiskAndChance', 'RiskAndChance'),
    ];
    measureProgress.Columns = measureProgressColumns;

    const measureTask: Query = new Query();
    measureTask.Name = 'xmtMeasureTask';
    measureTask.OPath = 'MeasureTasks';
    const measureTaskColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('TotalTimeRequired', 'TotalTimeRequired'),
      this.coreDataService.createQueryColumn('TotalTimeExecuted', 'TotalTimeExecuted'),
    ];
    measureTask.Columns = measureTaskColumns;
    measure.ObjectQueries = [measureProgress, measureTask];
    project.ObjectQueries = [measure];
    this.appService.subProjectRequestColumns.ObjectQueries = [project];
  }
  public checkSubprojectOpath(): string {
    let oPathExpression = `ParentId='${localStorage.getItem('projectId')}'`;
  
    for (let role of this.dataService.userRoles) {
      switch (role) {
        case this.ROLES.SubprojectDeputy:
          oPathExpression = `ParentId='${localStorage.getItem('projectId')}' And IdRefResponsibleSupport='${sessionStorage.getItem('profileId')}'`;
          break; 
        case this.ROLES.SubprojectResponsible:
          oPathExpression = `ParentId='${localStorage.getItem('projectId')}' And IdRefresponsible='${sessionStorage.getItem('profileId')}'`;
          break;
      }
    }
  
    return oPathExpression; 
  }
  private prepareAllSubProjects(): void {
    const subProjectColumns = [this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None)];
    const oPath = this.checkSubprojectOpath();
    this.dataService.readObjects<SubProject[]>(ObjectKeys.SUBPROJECT, subProjectColumns, oPath).subscribe(
      (subProjects) => {
        this.appService.subProjectIds = [];
        if (Array.isArray(subProjects) && subProjects.length > 0) {
          subProjects.forEach((subProject, index) => {
            if (this.subProjectId === subProject.Id) this.appService.currentSubProjectIndex = index;
            this.appService.subProjectIds.push(subProject.Id);
          });
        }
      },
      (error) => {
        console.error(error);
        this.appService.showLoader(false);
      },
      () => {
        this.appService.showLoader(false);
      }
    );
  }

  public backToSubProjectList(): void {
    this.router.navigateByUrl('subProject');
  }

  public goToNext(index: number): void {
    this.appService.showLoader(true);
    this.appService.currentSubProjectIndex = this.appService.currentSubProjectIndex + index;
    sessionStorage.setItem('subProjectId', this.appService.subProjectIds[this.appService.currentSubProjectIndex]);
    this.router.navigate(['subProjectDetail', this.appService.subProjectIds[this.appService.currentSubProjectIndex]]);
    if(this.gallery?.instance){
    index === 1 ? this.gallery.instance.nextItem(true) : this.gallery.instance.prevItem(true);
    this.displayReport();}
    this.appService.showLoader(false);
  }

  public prepareContextMenu(event): void {
    if (event.row.rowType === 'data') {
      event.items = [
        {
          text: this.dataService.res('Ubt-measure-Go-To') + ' ' + event.row.data?.measureTitle,
          icon: 'assets/images/measure-context-menu.svg',
          onItemClick: (e) => {
            this.appService.reportIndex = this.gallery.selectedIndex;
            this.router.navigate(['measureDetail', 'subProject', event.row.data?.measureId]);
          },
        },
      ];
    }
  }

  public onCellClick = (e) => {
    if (e.columnIndex !== 5 && e.rowType === 'data') {
      this.router.navigate(['measureDetail', 'subProject', e.row.data?.measureId]);
    }
  };

  public ngOnDestroy(): void {
    sessionStorage.removeItem('subProjectId');
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
export class SubProjectReport {
  measureId?: string;
  implementationStatus?: string;
  measureTitle?: string;
  progress?: string;
  nextSteps?: string;
  riskAndChance?: string;
  decision?: string;
  progressPercentage?: number;
  maturityLevel?: string;
  totalTime?: number = 0;
  executedTime?: number = 0;
  subProjectLabel?: string;
  responsible?: Person;

  constructor(data: SubProjectReport) {
    Object.assign(this, data);
  }
}

export enum StatePath {
  GRAY = 'assets/images/gray.svg',
  GREEN = 'assets/images/green.svg',
  YELLOW = 'assets/images/yellow.svg',
  RED = 'assets/images/red.svg',
}
